import { isArray, isString } from "underscore";
import type { Instance } from "mobx-state-tree";
import { types, flow, isAlive, getParent } from "mobx-state-tree";

import Constants from "const/constants";
import { setPublicRuntimeConfigToSecuredConfig } from "config/config";
import UploadService from "api/upload";
import type UploaderStore from "./uploader";

const { uploadStatusTypes } = Constants;

const wait = (time: number) =>
    new Promise((resolve) => setTimeout(resolve, time));

const previewableTypes = [`image`, `video`];

const UploadModel = types.model("UploadModel", {
    id: types.identifier,
    fileName: types.string,
    fileSize: types.maybe(types.number),
    status: types.optional(
        types.enumeration(`Status`, Object.values(uploadStatusTypes)),
        uploadStatusTypes.WAITING
    ),
    progress: types.optional(
        types.refinement(types.number, (value) => value >= 0 && value <= 1),
        0
    ),
    errorMessages: types.maybe(types.array(types.string)),
    title: types.maybe(types.string),
    description: types.maybe(types.string),
    storedURL: types.maybe(types.string),
    mimetype: types.maybe(types.string),
    duration: types.maybe(types.number),
    aspectRatio: types.maybe(types.string),
    tags: types.array(types.string),
    numberOfAudioStreams: types.maybe(types.number),
    numberOfVideoStreams: types.maybe(types.number),
    organizationId: types.maybe(types.string),
    thumbnailUrl: types.maybe(types.string)
});

const UploadStore = UploadModel.volatile(
    (): {
        uploadService: UploadService;
    } => ({
        uploadService: null
    })
)
    .views((self) => ({
        /**
         * @property
         * @summary - Invalidity of an upload
         * @returns {Boolean}
         */
        get invalid(): boolean {
            return (
                self.status === uploadStatusTypes.FAILED ||
                self.status === uploadStatusTypes.CANCELLED ||
                self.status === uploadStatusTypes.INVALID
            );
        },

        /**
         * @property
         * @summary - Progress, as a percentage string
         * @returns {String}
         */
        get progressPercentage(): string {
            return `${Math.floor(self.progress * 100)}%`;
        },

        /**
         * @property
         * @summary - Main type, derived from set mimetype string
         * @returns {String}
         */
        get type(): string {
            return self.mimetype?.split(`/`)[0];
        },

        /**
         * @property
         * @summary - Subtype, derived from set mimetype string
         * @returns {String}
         */
        get subtype(): string {
            return self.mimetype?.split(`/`)[1];
        },

        /**
         * @property
         * @summary - Filename as stored in S3
         * @returns {String}
         */
        get s3Filename(): string {
            return self.storedURL?.split(`/`).pop();
        },

        /**
         * @property
         * @summary - Display status
         * @returns {String}
         */
        get displayStatus(): string {
            switch (self.status) {
                case uploadStatusTypes.VALID:
                    return `Complete`;
                case uploadStatusTypes.FAILED:
                    return `Invalid`;
                default:
                    return self.status;
            }
        }
    }))
    .actions((self) => ({
        /**
         * @method
         * @summary - Updates Upload's status
         * @param {String} status Upload's status
         */
        setStatus(status: string) {
            self.status = status;
        },
        /**
         * @method
         * @summary - Updates Upload's progress value
         * @param {Number} progress Number between 0 - 1
         */
        setProgress(progress: number) {
            self.progress = progress;
        },
        /**
         * @method
         * @summary - Sets the errors for an Upload
         * @param {Array<String>} e Errors
         */
        setErrors(e: string[]) {
            if (self.errorMessages) {
                self.errorMessages.replace(e);
            } else {
                //@ts-ignore
                self.errorMessages = e;
            }
        }
    }))
    .actions((self) => ({
        /**
         * @method
         * @summary - Base Upload type start method. Should be overwritten when defining Upload types.
         */
        start() {
            throw new Error(`start() is not defined`);
        },
        /**
         * @method
         * @summary - Base Upload type stop method.
         */
        stop() {
            self.setStatus(uploadStatusTypes.CANCELLED);
            self.setProgress(0);
        },
        /**
         * @method
         * @summary - Updates Upload's file name
         * @param {String} name Updated file name
         */
        setFilename(name: string) {
            self.fileName = name;
        },
        /**
         * @method
         * @summary - Updates Upload's title metadata
         * @param {String} title Title
         */
        setTitle(title: string) {
            self.title = title.replace(/;/g, ``);
        },

        /**
         * @method
         * @summary - Updates the tags of the upload
         * @param {Array<String>} tags List of tags
         */
        setTags(tags: string[]) {
            self.tags.replace(tags);
        },
        /**
         * @method
         * @summary - Updates Upload's description metadata
         * @param {String} description Description
         */
        setDescription(description: string) {
            self.description = description;
        },

        setDuration(strDuration: string) {
            self.duration = strDuration && parseFloat(strDuration) * 1000;
        },

        setAspectRatio(strAspectRatio: string) {
            if (!strAspectRatio) return null;
            const numberAspectRatio =
                strAspectRatio &&
                parseFloat(parseFloat(strAspectRatio).toFixed(2));

            let aspectRatio;

            if (numberAspectRatio === 1.77)
                aspectRatio = Constants.ASPECT_RATIO_16_9_STRING;
            if (numberAspectRatio === 1)
                aspectRatio = Constants.ASPECT_RATIO_1_1_STRING;
            if (numberAspectRatio === 0.56)
                aspectRatio = Constants.ASPECT_RATIO_9_16_STRING;
            if (numberAspectRatio === 0.8)
                aspectRatio = Constants.ASPECT_RATIO_4_5_STRING;
            if (numberAspectRatio === 2)
                aspectRatio = Constants.ASPECT_RATIO_2_1_STRING;

            self.aspectRatio = aspectRatio;
        },

        /**
         * @method
         * @summary - Parses and sets errors, updates upload status
         * @param {String|Object|Array<String>} e Error(s)
         */
        catchUploadErrors(e) {
            if (!isAlive(self)) {
                return;
            }

            if (
                self.status !== uploadStatusTypes.INVALID &&
                self.status !== uploadStatusTypes.CANCELLED
            ) {
                self.setStatus(uploadStatusTypes.FAILED);
            }

            // Override error message for 400s
            if (e?.response?.status === 400) {
                self.setErrors([`Invalid file type`]);
                return;
            }

            if (isString(e)) {
                self.setErrors([e]); // Generic error
            } else if (isString(e?.message)) {
                self.setErrors([e.message]); // Endpoint error e.g. 400
            } else if (!isArray(e) && !isString(e)) {
                self.setErrors([`An unexpected error has occured.`]); // Unexpected error
            } else {
                self.setErrors(e as string[]); // Validation error
            }
        },

        /**
         * @method
         * @summary - Set the upload's file size
         * @param {Number} size In bytes
         */
        setFileSize(size: number) {
            self.fileSize = size;
        },

        setUploadService(service) {
            self.uploadService = service;
        },

        /**
         * @summary - Get an UploadService instance. This is async so that we can lazy-load AWS keys
         */
        getUploadService: flow(function* getUploadService() {
            if (self.uploadService) {
                return self.uploadService;
            } else {
                // First parent is a map, then the parent of that is and UploaderStore
                // We use getParent instead of getParentOfType to avoid circular imports
                const parent = getParent<Instance<typeof UploaderStore>>(
                    self,
                    2
                );
                if (parent?.uploadService) {
                    self.uploadService = parent.uploadService;
                    return self.uploadService;
                } else {
                    yield setPublicRuntimeConfigToSecuredConfig();
                    self.uploadService = new UploadService(self.organizationId);
                    return self.uploadService;
                }
            }
        })
    }));

export default UploadStore;

export const RemoteFileStore = types
    .compose(
        UploadStore,
        types.model({
            url: types.maybe(types.string)
        })
    )
    .views((self) => ({
        /**
         * @property
         * @summary - Should show preview if possibly compatible type and is valid
         * @returns {Boolean}
         */
        get shouldShowPreview(): boolean {
            return !!(
                previewableTypes.includes(self.type) &&
                self.status === uploadStatusTypes.VALID
            );
        },

        /**
         * @property
         * @summary - Attempt to preview from original location
         * @returns {String}
         */
        get previewURL(): string {
            return self.url;
        },

        /**
         * @property
         * @summary - Returns the path to the asset
         * @returns {String}
         */
        get pathname(): string {
            return self.url && decodeURIComponent(new URL(self.url).pathname);
        },

        /**
         * @property
         * @summary - Returns the href of the asset
         * @returns {String}
         */
        get href(): string {
            const url = new URL(self.storedURL || self.url);
            return url.href;
        }
    }))
    .actions((self) => ({
        /**
         * @method
         * @summary - Processes a remote file for import. Treats the file as uploaded, validating it from a remote location. If the file cannot be validated properly, it will be uploaded to S3 and revalidated from that location.
         * @param {String} context Validation context string
         * @returns {Promise}
         */
        start: flow(function* start(context) {
            try {
                const uploadService: UploadService =
                    yield self.getUploadService();

                self.setStatus(uploadStatusTypes.VALIDATING);

                // Check if remote file is valid
                const { isValid, errors, revalidate } =
                    yield uploadService.remoteFileIsValid(self.url, context);

                /**
                 * @note - This if clause is here to support legacy image storage requirements.
                 * Ideally this would be removed and we would only ever pass the remote URL to relevant endpoints for the import of image assets.
                 * That is the approach that should be taken when using these stores, rather than relying on the image being stored in S3 before import.
                 */
                if (isValid && self.type === `image`) {
                    self.setStatus(uploadStatusTypes.UPLOADING);

                    const res = yield fetch(self.url);
                    const blob = yield res.blob();
                    const { url } = yield uploadService.uploadFile(
                        blob,
                        (progress) => self.setProgress(progress / 100)
                    );

                    self.storedURL = url;
                }

                self.setProgress(1);

                // If valid, update status
                if (isValid) {
                    self.setStatus(uploadStatusTypes.VALID);
                }

                // If not valid, but can be revalidated
                if (!isValid && revalidate) {
                    self.setStatus(uploadStatusTypes.UPLOADING);

                    // Upload file to S3 and revalidate
                    // Should only have storedURL at this point in case of image
                    if (!self.storedURL) {
                        const { url: storedURL } =
                            yield uploadService.uploadFileFromURL(self.url);
                        self.storedURL = storedURL;
                    }

                    const { isValid, errors } =
                        yield uploadService.remoteFileIsValid(
                            self.storedURL,
                            context
                        );

                    // If not valid, throw errors and show error state
                    // Otherwise everything is OK
                    if (!isValid) {
                        self.setStatus(uploadStatusTypes.INVALID);
                        throw errors;
                    } else {
                        self.setStatus(uploadStatusTypes.VALID);
                    }
                }

                // If not valid and can't be revalidated, throw errors and show error state
                if (!isValid && !revalidate) {
                    self.setStatus(uploadStatusTypes.INVALID);
                    throw errors;
                }
            } catch (e) {
                self.catchUploadErrors(e);
            }
        }),

        /**
         * @method
         * @summary - Sets the URL for of remote file
         * @param {String} url URL
         */
        setUrl(url: string) {
            self.url = url;
        }
    }));

export const FileStore = types
    .compose(
        UploadStore,
        types.model({
            fileSize: types.number, //UploadStore already provides this...
            url: types.maybe(types.string) //@TODO: not sure if this is a mistake or required
        })
    )
    .views((self) => ({
        /**
         * @property
         * @summary - Should show preview if possibly compatible type, is stored and is valid
         * @returns {Boolean}
         */
        get shouldShowPreview(): boolean {
            return !!(
                previewableTypes.includes(self.type) &&
                self.storedURL &&
                self.status === uploadStatusTypes.VALID
            );
        },

        /**
         * @property
         * @summary - Attempt to preview from storedURL
         * @returns {Boolean}
         */
        get previewURL(): string {
            return self.storedURL;
        },

        /**
         * @property
         * @summary - Returns the path to the asset
         * @returns {String}
         */
        get pathname(): string {
            return (
                self.storedURL &&
                decodeURIComponent(new URL(self.storedURL).pathname)
            );
        },

        /**
         * @property
         * @summary - Returns the href of the asset
         * @returns {String}
         */
        get href(): string {
            const url = new URL(self.storedURL || self.url);
            return url.href;
        }
    }))
    .volatile(() => ({
        blob: null
    }))
    .actions((self) => ({
        beforeDestroy: flow(function* beforeDestroy() {
            const uploadService: UploadService = yield self.getUploadService();
            if (uploadService.uploads[self.id]) {
                uploadService.stopUpload(self.id);
            }
        }),

        /**
         * @method
         * @summary - Processes a local file for import. Validates it and uploads it to S3
         * @param {String} context Validation context string
         * @returns {Promise}
         */
        start: flow(function* start(context) {
            try {
                const uploadService: UploadService =
                    yield self.getUploadService();
                // Check if local file is valid
                self.setStatus(uploadStatusTypes.VALIDATING);
                const { isValid, errors, metadata } =
                    yield uploadService.localFileIsValid(self.blob, context);

                // If not valid, throw errors and show error state
                if (!isValid) {
                    self.setStatus(uploadStatusTypes.INVALID);
                    throw errors;
                }

                self.setStatus(uploadStatusTypes.UPLOADING);

                // Otherwise, store file on S3, updating progress
                const { url, mimetype } = yield uploadService.uploadFile(
                    self.blob,
                    (progress) => self.setProgress(progress / 100),
                    self.id
                );

                // Update file metadata once stored
                self.storedURL = url;
                self.mimetype = mimetype;

                /**
                 * @todo Audit whether aspect ratio/duration are actually needed
                 */
                if (metadata.media?.track) {
                    self.setAspectRatio(
                        getDisplayRatio(metadata.media.track[1])
                    );
                    self.setDuration(metadata.media.track[1].Duration);
                    self.numberOfAudioStreams =
                        parseInt(metadata.media.track[0].AudioCount) || 0;
                    self.numberOfVideoStreams =
                        parseInt(metadata.media.track[0].VideoCount) || 0;
                }

                self.setProgress(1);
                self.setStatus(uploadStatusTypes.UPLOADED);

                yield wait(650);

                self.setStatus(uploadStatusTypes.VALID);
            } catch (e) {
                self.catchUploadErrors(e);
            }
        }),

        /**
         * @method
         * @summary - Set local upload's file
         * @param {Blob} blob Sets the file for a local upload
         */
        setFile(blob) {
            self.blob = blob;
        },

        /**
         * @method
         * @summary - Stop the local file upload
         */
        stop: flow(function* stop() {
            const uploadService: UploadService = yield self.getUploadService();
            self.setStatus(uploadStatusTypes.CANCELLED);
            self.setProgress(0);

            if (uploadService.uploads[self.id]) {
                uploadService.stopUpload(self.id);
            }
        })
    }));

const getDisplayRatio = (track) => {
    if (track?.DisplayAspectRatio) return track.DisplayAspectRatio;

    if (track?.Height && track?.Width) {
        return track.Width / track.Height;
    }

    return null;
};

export interface UploadStoreInstance extends Instance<typeof UploadStore> {}
export type UploaderRemoteFileType = Instance<typeof RemoteFileStore>;
export type UploaderFileType = Instance<typeof FileStore>;
export type UploadType = Instance<typeof UploadModel>;
